import { useEffect } from 'react';

export const SITE_NAME = 'DarkNews';
export const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://darknews.ai';
export const DEFAULT_DESCRIPTION =
  'DarkNews — Anti-clickbait, anti-partisan news platform powered by AI and independent sources';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.svg`;

const MANAGED_SELECTOR = 'data-page-meta';

function upsertMeta(attrName, attrValue, content, id) {
  if (!content) {
    return;
  }
  let el = document.head.querySelector(`meta[${attrName}="${attrValue}"][${MANAGED_SELECTOR}]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    el.setAttribute(MANAGED_SELECTOR, 'true');
    if (id) {
      el.id = id;
    }
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) {
    return;
  }
  let el = document.head.querySelector(`link[rel="${rel}"][${MANAGED_SELECTOR}]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED_SELECTOR, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeManagedJsonLd() {
  document.querySelectorAll(`script[type="application/ld+json"][${MANAGED_SELECTOR}]`).forEach((node) => {
    node.remove();
  });
}

function upsertJsonLd(data) {
  removeManagedJsonLd();
  if (!data) {
    return;
  }
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(MANAGED_SELECTOR, 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function buildCanonicalUrl(path) {
  if (!path) {
    return SITE_URL;
  }
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

/**
 * Sets document title, description, Open Graph / Twitter tags, canonical URL,
 * and optional JSON-LD for the active route.
 */
export function usePageMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  jsonLd,
} = {}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  const canonical = buildCanonicalUrl(path);
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
    upsertLink('canonical', canonical);
    upsertJsonLd(jsonLd);
  }, [fullTitle, description, canonical, image, type, jsonLd, jsonLdKey]);
}

export default usePageMeta;
